import { useState } from "react";
import { Link } from "react-router";
import { TabBar } from "../../components/TabBar";

interface PracticeHubScreenProps {
  /** Id and name: a session needs the id, a chip shows the name. */
  subjects: { id: string; name: string }[];
  lastMockScore?: string | undefined;
  dueForReview: number;
  offlineSubject?: string | undefined;
  /** False in the WAEC profile: the mock is a JAMB CBT. */
  showMock?: boolean | undefined;
}

const MODES = [
  {
    to: "/practice/years",
    title: "Past Questions",
    blurb: "Real exam questions, year by year — objective and theory.",
  },
  {
    to: "/practice/topics",
    title: "Topic Practice",
    blurb: "Target one topic till it sticks — your weak ones first.",
  },
];

/** Screen 16 — subject chips, three modes, and the review queue. */
export function PracticeHubScreen({
  subjects,
  lastMockScore,
  dueForReview,
  offlineSubject,
  showMock = true,
}: PracticeHubScreenProps) {
  const [active, setActive] = useState(subjects[0]);

  return (
    <div className="app-column">
      <div className="flex-1 px-5 pb-6 pt-6">
        <h1 className="m-0 font-[family-name:var(--font-display)] text-[22px] font-bold">
          Practice
        </h1>

        <ul className="m-0 flex list-none gap-2 overflow-x-auto p-0 pt-4">
          {subjects.map((subject) => (
            <li key={subject.id}>
              <button
                type="button"
                aria-pressed={subject.id === active?.id}
                onClick={() => setActive(subject)}
                className={`min-h-10 whitespace-nowrap rounded-[var(--radius-chip)] border px-3.5 py-2 text-[13px] ${
                  subject.id === active?.id
                    ? "border-accent bg-accent-tint font-semibold"
                    : "border-border bg-surface text-text-muted"
                }`}
              >
                {subject.name}
              </button>
            </li>
          ))}
        </ul>

        <div className="flex flex-col gap-2.5 pt-5">
          {MODES.map((mode) => (
            <Link
              key={mode.to}
              to={mode.to}
              state={{ subject: active?.name, subjectId: active?.id }}
              className="flex flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-surface p-4"
            >
              <span className="text-[14.5px] font-semibold">{mode.title}</span>
              <span className="text-[12.5px] leading-[1.45] text-text-muted">{mode.blurb}</span>
            </Link>
          ))}

          {showMock ? (
            <Link
              to="/mock"
              className="flex flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-surface p-4"
            >
              <span className="text-[14.5px] font-semibold">Mock CBT</span>
              <span className="tabular text-[12.5px] leading-[1.45] text-text-muted">
                Full exam, real timing.
                {lastMockScore ? ` Last mock: ${lastMockScore}` : " You haven't sat one yet."}
              </span>
            </Link>
          ) : null}
        </div>

        {dueForReview > 0 ? (
          <Link
            to="/practice/review"
            className="mt-5 flex items-center justify-between rounded-[var(--radius-card)] border border-accent bg-accent-tint p-4"
          >
            <span className="tabular text-[13.5px]">
              <span className="font-semibold">{dueForReview} questions</span> are due for review
              today.
            </span>
            <span className="text-[13px] font-semibold text-accent">Review</span>
          </Link>
        ) : null}

        {offlineSubject ? (
          <p className="m-0 pt-5 text-[12px] text-text-muted">
            {offlineSubject} works offline ·{" "}
            <Link to="/downloads" className="font-semibold text-accent">
              Manage downloads
            </Link>
          </p>
        ) : null}
      </div>

      <TabBar />
    </div>
  );
}
